import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Coins, Loader2, Search } from 'lucide-react'
import {
  searchBillingUsers, userUsage,
  type AdminUserUsage, type BillingUser, type UsageBucket,
} from '@/api/billingAdmin'
import { formatCredits } from '@/api/billing'
import { InfoBox, Td, Th, chipStyle, errText, tableStyle } from '../adminUi'

const RANGES = [7, 14, 30, 60, 90]

/**
 * One user's AI consumption, in BOTH units.
 *
 * Everywhere else in the product a user sees credits and only credits. This
 * panel is the exception, and the reason it exists: the operator is the person
 * who has to answer "what did we bill them, and what did it cost us to serve" —
 * so every figure here is shown as credits billed alongside the provider
 * dollars behind it.
 */
export default function UserUsagePanel() {
  const [query, setQuery] = useState('')
  const [users, setUsers] = useState<BillingUser[]>([])
  const [selected, setSelected] = useState<BillingUser | null>(null)
  const [days, setDays] = useState(30)
  const [data, setData] = useState<AdminUserUsage | null>(null)
  const [searching, setSearching] = useState(false)
  const [loading, setLoading] = useState(false)

  const search = useCallback(async () => {
    setSearching(true)
    try {
      setUsers((await searchBillingUsers(query.trim() || undefined)).users)
    } catch (e) {
      toast.error(errText(e, 'Could not search users'))
    } finally {
      setSearching(false)
    }
  }, [query])

  // Seed the list so the panel is usable before anyone types.
  useEffect(() => { void search() }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selected) { setData(null); return }
    let live = true
    setLoading(true)
    userUsage(selected.user_id, { days })
      .then((d) => { if (live) setData(d) })
      .catch((e) => { if (live) toast.error(errText(e, 'Could not load usage')) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [selected, days])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <InfoBox>
        Users are shown credits only. This is the one place the provider dollars
        behind those credits are visible, at the current conversion rate.
      </InfoBox>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 240, maxWidth: 360 }}>
          <Search size={14} style={{
            position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--color-text-secondary)',
          }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void search() }}
            placeholder="Search by email or name…"
            style={{ width: '100%', paddingLeft: 28 }}
          />
        </div>
        <button className="btn btn-secondary" onClick={search} disabled={searching}>
          {searching ? <Loader2 size={14} className="spin" /> : 'Search'}
        </button>
        <select
          value={selected?.user_id || ''}
          onChange={(e) => setSelected(users.find((u) => u.user_id === e.target.value) || null)}
          style={{ minWidth: 260 }}
        >
          <option value="">Select a user…</option>
          {users.map((u) => (
            <option key={u.user_id} value={u.user_id}>
              {u.email}{u.user_name ? ` — ${u.user_name}` : ''}{u.is_active ? '' : ' (inactive)'}
            </option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: '0.25rem', marginLeft: 'auto' }}>
          {RANGES.map((d) => (
            <button
              key={d}
              className={`btn btn-sm ${d === days ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setDays(d)}
              style={{ padding: '0.15rem 0.6rem', fontSize: '0.8125rem' }}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {!selected && (
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem' }}>
          Pick a user to see what their AI has consumed.
        </p>
      )}

      {selected && loading && (
        <div style={{ padding: '2rem', textAlign: 'center' }}><Loader2 className="spin" /></div>
      )}

      {selected && !loading && data && <UsageReport data={data} days={days} />}
    </div>
  )
}

function UsageReport({ data, days }: { data: AdminUserUsage; days: number }) {
  const { summary, details, wallet } = data
  const rate = data.credits_per_usd

  const maxCredits = useMemo(
    () => Math.max(...details.daily.map((d) => d.credits), 0.0001),
    [details.daily],
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Headline: balance, plan, and what the window cost in both units. */}
      <div style={{
        display: 'grid', gap: '0.75rem',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      }}>
        <Stat
          label="Credit balance"
          value={wallet ? formatCredits(wallet.balance) : '—'}
          sub={wallet ? `${wallet.plan_label} · billed to ${wallet.scope}` : undefined}
          accent
        />
        <Stat
          label={`Credits used (${days}d)`}
          value={formatCredits(details.totals.credits)}
          sub={`${details.totals.calls.toLocaleString()} calls`}
        />
        <Stat
          label={`Provider cost (${days}d)`}
          value={`$${details.totals.cost_usd.toFixed(4)}`}
          sub={`at ${formatCredits(rate)} credits / $1`}
        />
        <Stat
          label="This month"
          value={formatCredits(summary.monthly.credits)}
          sub={`$${summary.monthly.cost_usd.toFixed(4)}`}
        />
        <Stat
          label="On the platform key"
          value={formatCredits(summary.platform.credits)}
          sub={`of ${formatCredits(summary.limits.monthly_credits)} allowed`}
        />
        <Stat
          label="On their own key"
          value={`$${summary.own.cost_usd.toFixed(4)}`}
          sub="never charged in credits"
        />
      </div>

      {details.daily.length > 0 && (
        <section>
          <SectionTitle>Daily consumption</SectionTitle>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 110 }}>
            {details.daily.map((d) => (
              <div
                key={d.date}
                title={`${d.date}: ${formatCredits(d.credits)} credits · $${d.cost_usd.toFixed(4)} · ${d.calls} calls`}
                style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}
              >
                <div style={{
                  height: `${Math.max((d.credits / maxCredits) * 100, 2)}%`,
                  background: 'var(--color-accent)', opacity: 0.8,
                  borderRadius: '2px 2px 0 0',
                }} />
              </div>
            ))}
          </div>
        </section>
      )}

      <Breakdown
        title="By model"
        rows={details.by_model}
        head="Model"
        cell={(r) => (
          <>
            <div style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{r.model}</div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{r.provider}</div>
          </>
        )}
      />
      <Breakdown
        title="By project"
        rows={details.by_project}
        head="Project"
        cell={(r) => <>{r.project}</>}
      />
      <Breakdown
        title="By task type"
        rows={details.by_task}
        head="Task"
        cell={(r) => <>{r.task_type}</>}
      />

      {details.recent.length > 0 && (
        <section>
          <SectionTitle>Recent calls</SectionTitle>
          <div style={{ maxHeight: 340, overflow: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <Th>When</Th><Th>Task</Th><Th>Model</Th><Th>Key</Th>
                  <Th right>Tokens</Th><Th right>Credits</Th><Th right>Cost</Th>
                </tr>
              </thead>
              <tbody>
                {details.recent.map((r) => (
                  <tr key={r.id}>
                    <Td>{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</Td>
                    <Td>{r.task_type}{r.obj_name ? ` · ${r.obj_name}` : ''}</Td>
                    <Td><span style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{r.model}</span></Td>
                    <Td><span style={chipStyle}>{r.source}</span></Td>
                    <Td right>{(r.input_tokens + r.output_tokens).toLocaleString()}</Td>
                    <Td right>{formatCredits(r.credits)}</Td>
                    <Td right>${r.cost_usd.toFixed(4)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  )
}

function Breakdown<T extends UsageBucket>({ title, rows, head, cell }: {
  title: string
  rows: T[]
  head: string
  cell: (row: T) => React.ReactNode
}) {
  if (!rows.length) return null
  return (
    <section>
      <SectionTitle>{title}</SectionTitle>
      <table style={tableStyle}>
        <thead>
          <tr>
            <Th>{head}</Th><Th right>Calls</Th><Th right>Tokens</Th>
            <Th right>Credits</Th><Th right>Cost</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <Td>{cell(r)}</Td>
              <Td right>{r.calls.toLocaleString()}</Td>
              <Td right>{(r.input_tokens + r.output_tokens).toLocaleString()}</Td>
              <Td right>{formatCredits(r.credits)}</Td>
              <Td right>${r.cost_usd.toFixed(4)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 style={{
      fontSize: '0.875rem', fontWeight: 600, margin: '0 0 0.5rem',
      display: 'flex', alignItems: 'center', gap: '0.35rem',
    }}>
      <Coins size={14} style={{ color: 'var(--color-accent)' }} />
      {children}
    </h3>
  )
}

function Stat({ label, value, sub, accent }: {
  label: string; value: string; sub?: string; accent?: boolean
}) {
  return (
    <div style={{
      border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
      padding: '0.75rem', background: 'var(--color-bg-secondary)',
    }}>
      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{label}</div>
      <div style={{
        fontSize: '1.3125rem', fontWeight: 700, marginTop: 2,
        fontVariantNumeric: 'tabular-nums',
        color: accent ? 'var(--color-accent)' : undefined,
      }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
          {sub}
        </div>
      )}
    </div>
  )
}
